// German TTS helper with bullet-proof cancellation.
//
// Two backends, picked once at startup:
//   • ElevenLabs (preferred) — when /api/tts/status returns enabled=true.
//     Audio is fetched from the backend (which caches MP3s on disk), so the
//     same line is served from local FS on every subsequent call.
//   • Web Speech API (fallback) — used when ElevenLabs is disabled, the
//     status fetch fails, audio playback errors out, or the browser is muted.
//
// To roll back to the original Web-Speech-only behaviour:
//   set ELEVEN_TTS_ENABLED="false" in /app/backend/.env (no code changes).
//
// Cancellation: every call to `cancel()` increments a module-wide `epoch`.
// Every running closure captures the epoch it was started in and aborts if
// the current epoch differs. Stale onend / setTimeout callbacks from an
// earlier screen can never continue after the screen has changed.

let epoch = 0;
let repeatTimer = null;
let mutedFlag = false;
let currentAudio = null; // active HTMLAudioElement when using ElevenLabs
let elevenStatus = { enabled: false, fetched: false };

// Initial language: read from localStorage (set by i18n) or fall back to en.
let currentLang = (() => {
    try {
        const v = localStorage.getItem("notfall.lang");
        if (v && /^[a-z]{2}$/i.test(v)) return v.toLowerCase();
    } catch {
        /* noop */
    }
    return "en";
})();

export const setLang = (code) => {
    if (typeof code === "string" && code.length > 0) currentLang = code;
};

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- ElevenLabs status (one-shot, cached) ----
const fetchElevenStatus = async () => {
    if (elevenStatus.fetched) return elevenStatus;
    try {
        const r = await fetch(`${API_BASE}/tts/status`);
        if (r.ok) {
            const d = await r.json();
            elevenStatus = { enabled: !!d.enabled, fetched: true };
        } else {
            elevenStatus = { enabled: false, fetched: true };
        }
    } catch {
        elevenStatus = { enabled: false, fetched: true };
    }
    return elevenStatus;
};
// Kick the status check immediately so the first call has it ready.
if (typeof window !== "undefined") fetchElevenStatus();

const LOCALE_MAP = {
    de: "de-DE",
    en: "en-US",
    tr: "tr-TR",
    uk: "uk-UA",
    hr: "hr-HR",
    bg: "bg-BG",
    ru: "ru-RU",
    ro: "ro-RO",
    pl: "pl-PL",
    sq: "sq-AL",
};
const currentLocale = () => LOCALE_MAP[currentLang] || "en-US";

const pickVoice = () => {
    if (!("speechSynthesis" in window)) return null;
    const voices = window.speechSynthesis.getVoices();
    const loc = currentLocale();
    const langPrefix = currentLang;
    return (
        voices.find((v) => v.lang && v.lang.toLowerCase() === loc.toLowerCase() && /google/i.test(v.name)) ||
        voices.find((v) => v.lang && v.lang.toLowerCase() === loc.toLowerCase()) ||
        voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(langPrefix)) ||
        null
    );
};

export const isMuted = () => mutedFlag;

const stopCurrentAudio = () => {
    if (currentAudio) {
        try {
            currentAudio.pause();
            currentAudio.src = "";
            currentAudio.load();
        } catch (_) {
            /* noop */
        }
        currentAudio = null;
    }
};

export const cancel = () => {
    epoch += 1;
    if (repeatTimer) {
        clearTimeout(repeatTimer);
        repeatTimer = null;
    }
    stopCurrentAudio();
    if ("speechSynthesis" in window) {
        try {
            window.speechSynthesis.cancel();
        } catch (_) {
            /* noop */
        }
    }
};

export const setMuted = (m) => {
    mutedFlag = !!m;
    if (mutedFlag) cancel();
};

// ---- Digit sanitiser: "112" → "Eins Eins Zwei" ----
const DIGIT_WORDS = {
    "0": "Null",
    "1": "Eins",
    "2": "Zwei",
    "3": "Drei",
    "4": "Vier",
    "5": "Fünf",
    "6": "Sechs",
    "7": "Sieben",
    "8": "Acht",
    "9": "Neun",
};
const sanitize = (text) => {
    if (!text) return text;
    return text.replace(/\b\d{3,6}\b/g, (match) =>
        match
            .split("")
            .map((d) => DIGIT_WORDS[d] || d)
            .join(" ")
    );
};

// ---- Web Speech (fallback) one-shot ----
const speakOnceWebSpeech = (text, onEnd, { rate = 0.9, myEpoch }) => {
    if (!("speechSynthesis" in window) || mutedFlag || myEpoch !== epoch) {
        onEnd?.();
        return;
    }
    try {
        window.speechSynthesis.cancel();
    } catch (_) {
        /* noop */
    }
    const utt = new SpeechSynthesisUtterance(sanitize(text));
    utt.lang = currentLocale();
    utt.rate = rate;
    utt.pitch = 1;
    utt.volume = 1;
    const v = pickVoice();
    if (v) utt.voice = v;
    const finish = () => {
        if (myEpoch !== epoch) return;
        onEnd?.();
    };
    utt.onend = finish;
    utt.onerror = finish;
    window.speechSynthesis.speak(utt);
};

// ---- ElevenLabs one-shot via <audio> ----
// `rate` is honoured via HTMLMediaElement.playbackRate (preserved pitch on
// modern browsers). Falls back to Web Speech on any error.
const speakOnceEleven = (text, onEnd, { rate = 1.0, myEpoch }) => {
    if (mutedFlag || myEpoch !== epoch) {
        onEnd?.();
        return;
    }
    const url = `${API_BASE}/tts?text=${encodeURIComponent(sanitize(text))}&lang=${encodeURIComponent(currentLang)}`;
    const audio = new Audio();
    audio.preload = "auto";
    audio.src = url;
    audio.playbackRate = rate;
    audio.preservesPitch = true;
    currentAudio = audio;

    const finish = (errored = false) => {
        if (currentAudio === audio) currentAudio = null;
        if (myEpoch !== epoch) return;
        if (errored) {
            // Hard fallback: try Web Speech for this line so user still hears it.
            speakOnceWebSpeech(text, onEnd, { rate: 0.9, myEpoch });
        } else {
            onEnd?.();
        }
    };
    audio.onended = () => finish(false);
    audio.onerror = () => finish(true);
    audio.play().catch(() => finish(true));
};

const speakOnceGuarded = (text, onEnd, opts) => {
    if (elevenStatus.enabled) {
        // Map our default Web-Speech rate (0.9) to a slightly slower playback
        // rate for ElevenLabs so the perceived speed stays similar across the
        // two backends. Burst lines pass their own rate explicitly.
        const r = opts.rate >= 1.3 ? Math.min(opts.rate * 0.85, 1.5) : 1.0;
        speakOnceEleven(text, onEnd, { ...opts, rate: r });
    } else {
        speakOnceWebSpeech(text, onEnd, opts);
    }
};

// Public: speak a single text, optionally auto-repeating every `repeatDelay`
// ms after it finishes. Returns a cancel function.
export const speak = (text, { repeat = false, repeatDelay = 3000, onEnd } = {}) => {
    cancel();
    const myEpoch = epoch;

    const start = async () => {
        await fetchElevenStatus();
        const run = () => {
            if (myEpoch !== epoch || mutedFlag) return;
            speakOnceGuarded(
                text,
                () => {
                    if (myEpoch !== epoch || mutedFlag) {
                        onEnd?.();
                        return;
                    }
                    if (repeat) {
                        repeatTimer = setTimeout(() => {
                            if (myEpoch !== epoch) return;
                            run();
                        }, repeatDelay);
                    } else {
                        onEnd?.();
                    }
                },
                { rate: 0.9, myEpoch }
            );
        };
        run();
    };
    start();
    return () => cancel();
};

// Public: speak a list of lines sequentially, optionally repeating the whole
// sequence after `repeatDelay` ms. Returns a cancel function.
export const speakSequence = (
    lines,
    { repeat = false, repeatDelay = 3000, onLineStart, onDone } = {}
) => {
    cancel();
    const myEpoch = epoch;

    const start = async () => {
        await fetchElevenStatus();
        const runSequence = () => {
            if (myEpoch !== epoch || mutedFlag) return;
            let i = 0;
            const nextLine = () => {
                if (myEpoch !== epoch || mutedFlag) return;
                if (i >= lines.length) {
                    if (myEpoch !== epoch) return;
                    onDone?.();
                    if (repeat) {
                        repeatTimer = setTimeout(() => {
                            if (myEpoch !== epoch) return;
                            runSequence();
                        }, repeatDelay);
                    }
                    return;
                }
                const line = lines[i];
                if (myEpoch !== epoch) return;
                onLineStart?.(i, line);
                i += 1;
                speakOnceGuarded(line, nextLine, { rate: 0.9, myEpoch });
            };
            nextLine();
        };
        runSequence();
    };
    start();
    return () => cancel();
};

// Fast, non-queueing "burst" speak for rhythm cues (CPR "Beatmen", reminders).
export const speakBurst = (text, { rate = 1.4 } = {}) => {
    if (mutedFlag) return;
    cancel();
    const myEpoch = epoch;
    if (elevenStatus.enabled) {
        // Pre-cached ElevenLabs MP3 plays back at adjusted rate.
        const playbackRate = rate >= 1.3 ? Math.min(rate * 0.85, 1.5) : 1.0;
        speakOnceEleven(text, () => {}, { rate: playbackRate, myEpoch });
        return;
    }
    if (!("speechSynthesis" in window)) return;
    const utt = new SpeechSynthesisUtterance(sanitize(text));
    utt.lang = currentLocale();
    utt.rate = rate;
    utt.pitch = 1;
    utt.volume = 1;
    const v = pickVoice();
    if (v) utt.voice = v;
    const guard = () => {
        if (myEpoch !== epoch) {
            try {
                window.speechSynthesis.cancel();
            } catch (_) {
                /* noop */
            }
        }
    };
    utt.onstart = guard;
    window.speechSynthesis.speak(utt);
};

// Ensure voices are loaded on Chromium (they load async)
if (typeof window !== "undefined" && "speechSynthesis" in window) {
    window.speechSynthesis.onvoiceschanged = () => {
        /* noop — triggers voice list load */
    };
}
